import { useQuery } from '@tanstack/react-query';
import { Select } from 'antd';
import { useState } from 'react';
import type { Paginated, PlaceDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * Pick a place from the gazetteer, searching as you type.
 *
 * ⚠️ It lives under `pages/Places/components/` and NOT under `components/`, because
 * CLAUDE.md §2 says `components/` is for shared primitives and this has exactly one
 * consumer today — the merge dialog. It moves up the moment a second page needs it,
 * which per FR-5.7 is the P2 arrangement form.
 *
 * Search is server-side and PREFIX-based: `search=espl` matches "Esplanade" through an
 * anchored regex over an indexed, pre-normalised term array. Ordering is by usage, so
 * the places people actually pick surface first — which is what makes a local-first
 * gazetteer feel better the more it is used, rather than worse.
 */
export const PlacePicker = ({
  value,
  onChange,
  excludeId,
  placeholder = 'Search places',
  disabled,
  'data-testid': testId,
}: {
  value?: string | undefined;
  onChange: (value: string | undefined) => void;
  /** Omitted from the options — a place is never merged into itself. */
  excludeId?: string;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
}) => {
  const [search, setSearch] = useState('');

  const params = { page: 1, limit: 20, ...(search.trim() ? { search: search.trim() } : {}) };
  const places = useQuery({
    queryKey: queryKeys.places.list(params),
    queryFn: ({ signal }) => api.get<Paginated<PlaceDto>>('/places', { query: params, signal }),
    staleTime: 30_000,
  });

  const options = (places.data?.items ?? [])
    .filter((place) => place.id !== excludeId)
    .map((place) => ({
      value: place.id,
      label: place.formattedAddress ? `${place.name} — ${place.formattedAddress}` : place.name,
    }));

  return (
    <div data-testid={testId}>
      <Select
        showSearch
        allowClear
        className="w-full"
        placeholder={placeholder}
        disabled={disabled}
        value={value}
        loading={places.isFetching}
        onChange={(next: string | undefined) => onChange(next)}
        onSearch={setSearch}
        // The server already prefix-matched; filtering again on the client would hide
        // alias matches, whose text never appears in the label.
        filterOption={false}
        options={options}
        notFoundContent={places.isFetching ? 'Searching…' : 'No places match.'}
      />
    </div>
  );
};
